import type { IntlShape } from 'react-intl'
import { SAVE_THOUSANDS_REGIONS } from 'luxPlus/constants/benefits'

/**
 * @param regionCode - current region code
 * @param intl - react-intl IntlShape for localisation
 * @returns save amount copy - depending on if the region has a high denomination currency such as ZAR, THB, etc.
 */
function getRegionSaveAmountCopy(regionCode: string, intl: IntlShape) {
  const isSaveThousandsRegion = SAVE_THOUSANDS_REGIONS.includes(regionCode)
  return isSaveThousandsRegion
    ? intl.formatMessage({
        id: 'pHMqUvu-GNMdaMhAT8Go',
        defaultMessage: 'thousands',
        description: 'Savings amount descriptor for high-denomination currency regions',
      })
    : intl.formatMessage({
        id: 'z1-tI-1zLx8QI1yE2yFF',
        defaultMessage: 'hundreds',
        description: 'Savings amount descriptor for standard currency regions',
      })
}

/**
 * Approximate equivalent of $450 AUD in each region's local currency.
 * Rounded to marketing-friendly numbers.
 */
const REGION_SAVE_AMOUNTS: Record<string, number> = {
  AU: 450,
  NZ: 490,
  US: 290,
  GB: 230,
  CA: 400,
  SG: 390,
  HK: 2250,
  IN: 24000,
  ZA: 5400,
  TH: 9700,
  KR: 414000,
  ID: 4600000,
  JP: 29000,
  TW: 9500,
  PH: 16500,
  VN: 7500000,
  CN: 2070,
  MY: 1340,
  AE: 1070,
  QA: 1065,
  SA: 1095,
  IL: 1070,
  MO: 2360,
  FR: 270,
  DE: 270,
  IE: 270,
  IT: 270,
  NL: 270,
  ES: 270,
}

const DEFAULT_SAVE_AMOUNT = 450

/**
 * @param regionCode - current region code
 * @returns the approximate save amount in the region's local currency (based on ~$450 AUD)
 */
export function getRegionSaveAmount(regionCode: string): number {
  return REGION_SAVE_AMOUNTS[regionCode] ?? DEFAULT_SAVE_AMOUNT
}

const AUD_BASE = 450

/**
 * Converts an AUD amount to the approximate local currency equivalent
 * using the ratio derived from the REGION_SAVE_AMOUNTS mapping.
 */
export function convertAudToRegion(audAmount: number, regionCode: string): number {
  const localEquivalent = REGION_SAVE_AMOUNTS[regionCode] ?? DEFAULT_SAVE_AMOUNT
  return Math.round(audAmount * (localEquivalent / AUD_BASE))
}

export default getRegionSaveAmountCopy
